import type { ServiceAccountWhere } from "@tillix/db/validators/ServiceAccountWhere";
import { computed } from "mobx";
import { _async, _await, modelFlow } from "mobx-keystone";
import { api } from "../../../orpc/client";
import { serviceAccountDbToSnapshot } from "../../../utils/db-to-snapshot";
import { compareByDateDesc, findNextDue } from "../../lib/compare-by-date";
import { createTStore, type TStoreInit } from "../../lib/models/create-t-store";
import { TStore } from "../../lib/models/t-store";
import { ServiceAccountModel } from "./model.service-account";
import { serviceAccountNS } from "./ns.service-account";
import type { CoreError } from "../../lib/error";
import type { Self } from "../../lib/models/ref";
import type { InvoiceModel } from "../invoice/model.invoice";

type ServiceAccountWhereInput = typeof ServiceAccountWhere.infer;

@serviceAccountNS.store
export class ServiceAccountStore extends TStore<
  ServiceAccountModel,
  ServiceAccountWhereInput,
  typeof api.serviceAccount
> {
  Model = ServiceAccountModel;
  api = api.serviceAccount;
  // Every screen (dashboard totals, pay, search, filters) works over the whole
  // list in memory, so follow hasMore and load every page. A customer with
  // many properties saw only the first 20 before.
  loadAllPages = true;

  protected _sync(): Promise<void> {
    throw new Error("Method not implemented.");
  }
  syncById(id: string): Promise<{}> {
    throw new Error("Method not implemented.");
  }
  updateById(id: string, data: Self<ServiceAccountModel>): Promise<{}> {
    throw new Error("Method not implemented.");
  }
  get ready(): boolean | CoreError[] {
    return true;
    // throw new Error("Method not implemented.");
  }

  @computed
  get isSyncing(): boolean {
    return this.data.some((account) => account.syncStatus === "SYNCING");
  }

  @computed
  get sortedByUpdatedAtDesc(): ServiceAccountModel[] {
    return this.data.toSorted((a, b) =>
      compareByDateDesc(a.updatedAt, b.updatedAt),
    );
  }

  @computed
  get totalBalance(): number {
    return this.data.reduce((sum, a) => sum + a.balance, 0);
  }

  @computed
  get nextDueAccount():
    | { account: ServiceAccountModel; invoice: InvoiceModel }
    | undefined {
    const invoices = this.tilliX.invoices;

    const candidates: {
      account: ServiceAccountModel;
      invoice: InvoiceModel;
    }[] = [];
    for (const account of this.sortedByUpdatedAtDesc) {
      const accountOpen = invoices.unpaidInvoices.filter(
        (i) => i.serviceAccountId === account.id,
      );
      const next = findNextDue(accountOpen);
      if (next) candidates.push({ account, invoice: next });
    }

    const best = findNextDue(
      candidates.map((c) => ({ ...c, dueAt: c.invoice.dueAt })),
    );
    return best ? { account: best.account, invoice: best.invoice } : undefined;
  }

  @modelFlow
  createServiceAccount = _async(function* _createServiceAccount(
    this: ServiceAccountStore,
    input: {
      nickname: string;
      externalId: string;
      meta: Record<string, unknown>;
    },
  ) {
    try {
      const nickname = input.nickname.trim();
      const created = yield* _await(
        this.api.post({
          nickname: nickname.length > 0 ? nickname : undefined,
          externalId: input.externalId,
          meta: input.meta,
        }),
      );

      this.addRaw([serviceAccountDbToSnapshot(created)]);

      return created;
    } catch (err) {
      console.error(
        "[ServiceAccountStore.createServiceAccount] API error:",
        err,
      );
      throw err;
    }
  });

  /**
   * Registers a contract account from the user's legacy-portal migration
   * record (no name/zip verification — authorized server-side against
   * `UserExpected`). Idempotent: an already-registered account is returned.
   */
  @modelFlow
  migrateServiceAccount = _async(function* _migrateServiceAccount(
    this: ServiceAccountStore,
    externalId: string,
  ) {
    try {
      const migrated = yield* _await(this.api.migrate({ externalId }));

      const existing = this.byId(migrated.id, true);
      if (existing) return existing;

      this.addRaw([serviceAccountDbToSnapshot(migrated)]);
      const model = this.byId(migrated.id, true);
      if (!model) {
        throw new Error("Migrated service account missing from store");
      }
      return model;
    } catch (err) {
      console.error(
        "[ServiceAccountStore.migrateServiceAccount] API error:",
        err,
      );
      throw err;
    }
  });

  @modelFlow
  deleteServiceAccount = _async(function* _deleteServiceAccount(
    this: ServiceAccountStore,
    id: string,
  ) {
    try {
      const result = yield* _await(this.api.delete({ id }));

      if (result.success) {
        const model = this.byId(id, true);
        if (model) {
          this.removeData(model);
        }
      }

      return result;
    } catch (err) {
      console.error("Error deleting service account: ", err);
      throw err;
    }
  });

  @modelFlow
  editServiceAccount = _async(function* _editServiceAccount(
    this: ServiceAccountStore,
    input: {
      id: string;
      nickname?: string | null;
      nameOnAccount?: string | null;
    },
  ) {
    try {
      const model = this.byId(input.id, true);
      if (!model) {
        throw new Error("Service account not in store");
      }

      const oldNameOnAccount = model.nameOnAccount;
      const nameChanged =
        "nameOnAccount" in input &&
        (input.nameOnAccount ?? null) !== (oldNameOnAccount ?? null);

      const newMeta = nameChanged
        ? {
            ...model.meta,
            nameChangeRequest: "processing",
            nameChangeRequestedAt: new Date().toISOString(),
            nameChangeOldName: oldNameOnAccount ?? "",
            nameChangeNewName: input.nameOnAccount ?? "",
          }
        : undefined;

      yield* _await(
        this.api.put({ ...input, ...(newMeta ? { meta: newMeta } : {}) }),
      );

      if ("nickname" in input) model.nickname = input.nickname ?? null;
      if (newMeta) model.meta = newMeta;

      return model;
    } catch (err) {
      console.error("Error editing service account: ", err);
      throw err;
    }
  });

  @modelFlow
  dismissNameChangeRequest = _async(function* _dismissNameChangeRequest(
    this: ServiceAccountStore,
    serviceAccountId: string,
  ) {
    const model = this.byId(serviceAccountId, true);
    if (!model) return;
    const {
      nameChangeRequest: _a,
      nameChangeRequestedAt: _b,
      nameChangeOldName: _c,
      nameChangeNewName: _d,
      ...rest
    } = model.meta;

    model.meta = rest;
    try {
      yield* _await(this.api.put({ id: serviceAccountId, meta: rest }));
    } catch (err) {
      console.error("Error dismissing name change request:", err);
    }
  });
}

export const createServiceAccountStore = (
  incomingData?: TStoreInit<ServiceAccountModel, ServiceAccountStore>,
) =>
  createTStore<ServiceAccountModel, ServiceAccountStore>(
    ServiceAccountStore,
    incomingData,
  );
